import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
} from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  IconButton,
  Surface,
  Button,
} from 'react-native-paper';
import { LineChart, BarChart, PieChart } from 'react-native-chart-kit';
import type { MasteryState } from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';

const { width: screenWidth } = Dimensions.get('window');

export interface ProgressTrendData {
  userId: string;
  timeRange: '7d' | '30d' | '90d' | '1y';
  overallTrend: TrendAnalysis;
  skillTrends: SkillTrendAnalysis[];
  categoryTrends: CategoryTrendAnalysis[];
  predictions: ProgressPrediction[];
  milestones: MilestoneTrend[];
  patterns: TrendPattern[];
  anomalies: TrendAnomaly[];
  lastUpdated: string;
}

export interface TrendAnalysis {
  direction: 'upward' | 'downward' | 'stable' | 'volatile';
  velocity: number; // rate of change per day
  acceleration: number; // change in velocity
  confidence: number;
  r2Score: number; // goodness of fit
  seasonality: SeasonalityInfo | null;
  breakpoints: TrendBreakpoint[];
}

export interface SkillTrendAnalysis {
  skillId: string;
  skillName: string;
  category: string;
  trend: TrendAnalysis;
  dataPoints: TrendDataPoint[];
  correlations: SkillCorrelation[];
  forecast: ForecastPoint[];
}

export interface CategoryTrendAnalysis {
  category: string;
  skillCount: number;
  averageTrend: TrendAnalysis;
  topPerformers: string[];
  strugglingSkills: string[];
  categoryVelocity: number;
}

export interface TrendDataPoint {
  timestamp: string;
  value: number;
  context?: string;
  confidence?: number;
  source: 'measured' | 'interpolated' | 'predicted';
}

export interface SkillCorrelation {
  skillId: string;
  skillName: string;
  correlation: number; // -1 to 1
  significance: number; // p-value
  relationship: 'positive' | 'negative' | 'none';
}

export interface ForecastPoint {
  timestamp: string;
  predictedValue: number;
  confidenceInterval: [number, number];
  factors: PredictionFactor[];
}

export interface PredictionFactor {
  name: string;
  impact: number;
  confidence: number;
}

export interface ProgressPrediction {
  type: 'milestone' | 'mastery' | 'plateau' | 'breakthrough';
  skillId?: string;
  description: string;
  predictedDate: string;
  confidence: number;
  probability: number;
  conditions: string[];
  recommendations: string[];
}

export interface MilestoneTrend {
  milestoneId: string;
  name: string;
  targetDate: string;
  predictedDate: string;
  completionProbability: number;
  requiredVelocity: number;
  currentVelocity: number;
  blockers: string[];
  accelerators: string[];
}

export interface TrendPattern {
  id: string;
  type: 'cyclical' | 'seasonal' | 'growth' | 'plateau' | 'decline';
  name: string;
  description: string;
  frequency: number; // days
  amplitude: number;
  phase: number;
  strength: number;
  examples: string[];
}

export interface TrendAnomaly {
  id: string;
  timestamp: string;
  skillId: string;
  type: 'spike' | 'drop' | 'plateau' | 'acceleration';
  magnitude: number;
  duration: number;
  possibleCauses: string[];
  impact: 'positive' | 'negative' | 'neutral';
}

export interface SeasonalityInfo {
  period: number; // days
  amplitude: number;
  phase: number;
  strength: number;
}

export interface TrendBreakpoint {
  timestamp: string;
  type: 'acceleration' | 'deceleration' | 'direction_change';
  significance: number;
  beforeVelocity: number;
  afterVelocity: number;
}

interface ProgressTrendAnalyzerProps {
  trendData: ProgressTrendData;
  masteryStates: MasteryState[];
  onSkillFocus: (skillId: string) => void;
  onPredictionExplore: (prediction: ProgressPrediction) => void;
  onAnomalyInvestigate: (anomaly: TrendAnomaly) => void;
  style?: any;
}

type ViewMode =
  | 'overview'
  | 'skills'
  | 'predictions'
  | 'patterns'
  | 'anomalies';
type TimeRange = '7d' | '30d' | '90d' | '1y';

export function ProgressTrendAnalyzer({
  trendData,
  masteryStates,
  onSkillFocus,
  onPredictionExplore,
  onAnomalyInvestigate,
  style,
}: ProgressTrendAnalyzerProps) {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('overview');
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>(
    trendData.timeRange
  );
  const [selectedSkill, setSelectedSkill] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Get trend direction color
  const getTrendColor = (direction: string) => {
    switch (direction) {
      case 'upward':
        return '#4CAF50';
      case 'downward':
        return '#F44336';
      case 'stable':
        return '#9E9E9E';
      case 'volatile':
        return '#FF9800';
      default:
        return theme.colors.outline;
    }
  };

  // Get trend icon
  const getTrendIcon = (direction: string) => {
    switch (direction) {
      case 'upward':
        return 'trending-up';
      case 'downward':
        return 'trending-down';
      case 'stable':
        return 'trending-neutral';
      case 'volatile':
        return 'pulse';
      default:
        return 'help-circle';
    }
  };

  // Prepare overall trend chart data
  const overallChartData = useMemo(() => {
    // Aggregate all skill trends for overall view
    const aggregatedData: { [key: string]: number[] } = {};

    trendData.skillTrends.forEach(skillTrend => {
      skillTrend.dataPoints.forEach(point => {
        const date = new Date(point.timestamp).toDateString();
        if (!aggregatedData[date]) {
          aggregatedData[date] = [];
        }
        aggregatedData[date].push(point.value);
      });
    });

    const labels = Object.keys(aggregatedData).sort().slice(-7); // Last 7 data points
    const data = labels.map(date => {
      const values = aggregatedData[date];
      return (values.reduce((sum, val) => sum + val, 0) / values.length) * 100;
    });

    return {
      labels: labels.map(date => {
        const d = new Date(date);
        return `${d.getMonth() + 1}/${d.getDate()}`;
      }),
      datasets: [
        {
          data,
          color: (opacity = 1) =>
            getTrendColor(trendData.overallTrend.direction) +
            Math.round(opacity * 255).toString(16),
          strokeWidth: 3,
        },
      ],
    };
  }, [trendData]);

  // Prepare skill-specific chart data
  const skillChartData = useMemo(() => {
    if (!selectedSkill) return null;

    const skillTrend = trendData.skillTrends.find(
      st => st.skillId === selectedSkill
    );
    if (!skillTrend) return null;

    const labels = skillTrend.dataPoints.map(dp => {
      const date = new Date(dp.timestamp);
      return `${date.getMonth() + 1}/${date.getDate()}`;
    });

    const actualData = skillTrend.dataPoints.map(dp => dp.value * 100);
    const forecastData = skillTrend.forecast.map(fp => fp.predictedValue * 100);

    return {
      labels: [...labels, ...skillTrend.forecast.map((_, i) => `+${i + 1}d`)],
      datasets: [
        {
          data: [
            ...actualData,
            ...Array(skillTrend.forecast.length).fill(null),
          ],
          color: (opacity = 1) => `rgba(103, 80, 164, ${opacity})`,
          strokeWidth: 2,
        },
        {
          data: [...Array(actualData.length).fill(null), ...forecastData],
          color: (opacity = 1) => `rgba(103, 80, 164, ${opacity * 0.5})`,
          strokeWidth: 2,
          strokeDashArray: [5, 5],
        },
      ],
    };
  }, [selectedSkill, trendData.skillTrends]);

  const renderOverview = () => (
    <ScrollView style={styles.overviewContainer}>
      {/* Overall Trend Card */}
      <Card style={styles.overallTrendCard}>
        <View style={styles.trendHeader}>
          <View style={styles.trendInfo}>
            <Text variant='headlineSmall' style={styles.trendTitle}>
              Overall Progress Trend
            </Text>
            <View style={styles.trendIndicator}>
              <IconButton
                icon={getTrendIcon(trendData.overallTrend.direction)}
                size={24}
                iconColor={getTrendColor(trendData.overallTrend.direction)}
              />
              <Text
                variant='titleMedium'
                style={[
                  styles.trendDirection,
                  { color: getTrendColor(trendData.overallTrend.direction) },
                ]}
              >
                {trendData.overallTrend.direction.toUpperCase()}
              </Text>
            </View>
          </View>
          <ConfidenceIndicator
            confidence={trendData.overallTrend.confidence}
            size='medium'
            showLabel={true}
          />
        </View>

        <View style={styles.trendMetrics}>
          <View style={styles.metricItem}>
            <Text variant='bodySmall' style={styles.metricLabel}>
              Velocity
            </Text>
            <Text variant='titleMedium' style={styles.metricValue}>
              {(trendData.overallTrend.velocity * 100).toFixed(1)}%/day
            </Text>
          </View>
          <View style={styles.metricItem}>
            <Text variant='bodySmall' style={styles.metricLabel}>
              Acceleration
            </Text>
            <Text
              variant='titleMedium'
              style={[
                styles.metricValue,
                {
                  color:
                    trendData.overallTrend.acceleration >= 0
                      ? '#4CAF50'
                      : '#F44336',
                },
              ]}
            >
              {trendData.overallTrend.acceleration >= 0 ? '+' : ''}
              {(trendData.overallTrend.acceleration * 100).toFixed(2)}
            </Text>
          </View>
          <View style={styles.metricItem}>
            <Text variant='bodySmall' style={styles.metricLabel}>
              R² Score
            </Text>
            <Text variant='titleMedium' style={styles.metricValue}>
              {trendData.overallTrend.r2Score.toFixed(3)}
            </Text>
          </View>
        </View>

        {/* Overall Trend Chart */}
        <LineChart
          data={overallChartData}
          width={screenWidth - 64}
          height={200}
          chartConfig={{
            backgroundColor: theme.colors.surface,
            backgroundGradientFrom: theme.colors.surface,
            backgroundGradientTo: theme.colors.surface,
            decimalPlaces: 0,
            color: (opacity = 1) =>
              getTrendColor(trendData.overallTrend.direction) +
              Math.round(opacity * 255).toString(16),
            labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity * 0.7})`,
            style: { borderRadius: 16 },
            propsForDots: {
              r: '4',
              strokeWidth: '2',
              stroke: getTrendColor(trendData.overallTrend.direction),
            },
          }}
          bezier
          style={styles.chart}
        />
      </Card>

      {/* Category Trends */}
      <Card style={styles.categoryTrendsCard}>
        <Text variant='titleMedium' style={styles.categoryTitle}>
          Category Performance
        </Text>
        {trendData.categoryTrends.map(category => (
          <TouchableOpacity
            key={category.category}
            style={styles.categoryItem}
            onPress={() => setSelectedCategory(category.category)}
          >
            <View style={styles.categoryInfo}>
              <Text variant='titleSmall' style={styles.categoryName}>
                {category.category}
              </Text>
              <Text variant='bodySmall' style={styles.categorySkillCount}>
                {category.skillCount} skills
              </Text>
            </View>
            <View style={styles.categoryTrend}>
              <IconButton
                icon={getTrendIcon(category.averageTrend.direction)}
                size={16}
                iconColor={getTrendColor(category.averageTrend.direction)}
              />
              <Text variant='bodyMedium' style={styles.categoryVelocity}>
                {(category.categoryVelocity * 100).toFixed(1)}%/day
              </Text>
            </View>
          </TouchableOpacity>
        ))}
      </Card>

      {/* Quick Predictions */}
      <Card style={styles.quickPredictionsCard}>
        <Text variant='titleMedium' style={styles.predictionsTitle}>
          Upcoming Milestones
        </Text>
        {trendData.predictions.slice(0, 3).map(prediction => (
          <TouchableOpacity
            key={prediction.description}
            style={styles.predictionItem}
            onPress={() => onPredictionExplore(prediction)}
          >
            <View style={styles.predictionContent}>
              <Text variant='titleSmall' style={styles.predictionDescription}>
                {prediction.description}
              </Text>
              <Text variant='bodySmall' style={styles.predictionDate}>
                Expected:{' '}
                {new Date(prediction.predictedDate).toLocaleDateString()}
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.predictionProbability,
                  {
                    color:
                      prediction.probability > 0.7
                        ? '#4CAF50'
                        : prediction.probability > 0.4
                          ? '#FF9800'
                          : '#F44336',
                  },
                ]}
              >
                {Math.round(prediction.probability * 100)}% probability
              </Text>
            </View>
            <IconButton icon='chevron-right' size={16} />
          </TouchableOpacity>
        ))}
      </Card>
    </ScrollView>
  );

  const renderSkills = () => (
    <ScrollView style={styles.skillsContainer}>
      {/* Skill Selector */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={styles.skillSelector}
      >
        {trendData.skillTrends.map(skillTrend => (
          <Chip
            key={skillTrend.skillId}
            selected={selectedSkill === skillTrend.skillId}
            onPress={() => setSelectedSkill(skillTrend.skillId)}
            style={styles.skillChip}
          >
            {skillTrend.skillName}
          </Chip>
        ))}
      </ScrollView>

      {/* Selected Skill Analysis */}
      {selectedSkill && skillChartData && (
        <Card style={styles.skillAnalysisCard}>
          <Text variant='titleMedium' style={styles.skillAnalysisTitle}>
            {
              trendData.skillTrends.find(st => st.skillId === selectedSkill)
                ?.skillName
            }{' '}
            Analysis
          </Text>

          <LineChart
            data={skillChartData}
            width={screenWidth - 64}
            height={220}
            chartConfig={{
              backgroundColor: theme.colors.surface,
              backgroundGradientFrom: theme.colors.surface,
              backgroundGradientTo: theme.colors.surface,
              decimalPlaces: 0,
              color: (opacity = 1) => `rgba(103, 80, 164, ${opacity})`,
              labelColor: (opacity = 1) => `rgba(0, 0, 0, ${opacity * 0.7})`,
              style: { borderRadius: 16 },
            }}
            bezier
            style={styles.chart}
          />

          {/* Skill Correlations */}
          {(() => {
            const skillTrend = trendData.skillTrends.find(
              st => st.skillId === selectedSkill
            );
            if (!skillTrend || skillTrend.correlations.length === 0)
              return null;

            return (
              <View style={styles.correlationsSection}>
                <Text variant='labelLarge' style={styles.correlationsTitle}>
                  Related Skills
                </Text>
                {skillTrend.correlations.slice(0, 3).map(correlation => (
                  <View
                    key={correlation.skillId}
                    style={styles.correlationItem}
                  >
                    <Text variant='bodyMedium' style={styles.correlationSkill}>
                      {correlation.skillName}
                    </Text>
                    <Text
                      variant='bodySmall'
                      style={[
                        styles.correlationValue,
                        {
                          color:
                            correlation.correlation > 0 ? '#4CAF50' : '#F44336',
                        },
                      ]}
                    >
                      {correlation.correlation > 0 ? '+' : ''}
                      {correlation.correlation.toFixed(2)}
                    </Text>
                  </View>
                ))}
              </View>
            );
          })()}
        </Card>
      )}

      {/* All Skills List */}
      <View style={styles.skillsList}>
        {trendData.skillTrends.map(skillTrend => (
          <Card key={skillTrend.skillId} style={styles.skillCard}>
            <TouchableOpacity
              onPress={() => onSkillFocus(skillTrend.skillId)}
              style={styles.skillCardContent}
            >
              <View style={styles.skillInfo}>
                <Text variant='titleSmall' style={styles.skillName}>
                  {skillTrend.skillName}
                </Text>
                <Text variant='bodySmall' style={styles.skillCategory}>
                  {skillTrend.category}
                </Text>
              </View>
              <View style={styles.skillTrendInfo}>
                <IconButton
                  icon={getTrendIcon(skillTrend.trend.direction)}
                  size={16}
                  iconColor={getTrendColor(skillTrend.trend.direction)}
                />
                <Text variant='bodySmall' style={styles.skillVelocity}>
                  {(skillTrend.trend.velocity * 100).toFixed(1)}%/day
                </Text>
              </View>
            </TouchableOpacity>
          </Card>
        ))}
      </View>
    </ScrollView>
  );

  const renderPredictions = () => (
    <ScrollView style={styles.predictionsContainer}>
      {trendData.predictions.map(prediction => (
        <Card key={prediction.description} style={styles.predictionCard}>
          <TouchableOpacity
            onPress={() => onPredictionExplore(prediction)}
            style={styles.predictionCardContent}
          >
            <View style={styles.predictionHeader}>
              <Text variant='titleMedium' style={styles.predictionTitle}>
                {prediction.description}
              </Text>
              <ConfidenceIndicator
                confidence={prediction.confidence}
                size='small'
                showLabel={false}
              />
            </View>

            <View style={styles.predictionDetails}>
              <Text variant='bodyMedium' style={styles.predictionDetailText}>
                Expected:{' '}
                {new Date(prediction.predictedDate).toLocaleDateString()}
              </Text>
              <Text
                variant='bodyMedium'
                style={[
                  styles.predictionProbabilityText,
                  {
                    color:
                      prediction.probability > 0.7
                        ? '#4CAF50'
                        : prediction.probability > 0.4
                          ? '#FF9800'
                          : '#F44336',
                  },
                ]}
              >
                {Math.round(prediction.probability * 100)}% probability
              </Text>
            </View>

            {prediction.conditions.length > 0 && (
              <View style={styles.conditionsSection}>
                <Text variant='labelMedium' style={styles.conditionsTitle}>
                  Conditions:
                </Text>
                {prediction.conditions.slice(0, 2).map((condition, index) => (
                  <Text
                    key={index}
                    variant='bodySmall'
                    style={styles.conditionText}
                  >
                    • {condition}
                  </Text>
                ))}
              </View>
            )}

            <IconButton
              icon='chevron-right'
              size={16}
              style={styles.predictionArrow}
            />
          </TouchableOpacity>
        </Card>
      ))}
    </ScrollView>
  );

  const renderPatterns = () => (
    <ScrollView style={styles.patternsContainer}>
      {trendData.patterns.map(pattern => (
        <Card key={pattern.id} style={styles.patternCard}>
          <View style={styles.patternContent}>
            <View style={styles.patternHeader}>
              <Text variant='titleMedium' style={styles.patternName}>
                {pattern.name}
              </Text>
              <Text variant='bodySmall' style={styles.patternType}>
                {pattern.type}
              </Text>
            </View>

            <Text variant='bodyMedium' style={styles.patternDescription}>
              {pattern.description}
            </Text>

            <View style={styles.patternMetrics}>
              <View style={styles.patternMetric}>
                <Text variant='bodySmall' style={styles.patternMetricLabel}>
                  Frequency
                </Text>
                <Text variant='bodyMedium' style={styles.patternMetricValue}>
                  {pattern.frequency} days
                </Text>
              </View>
              <View style={styles.patternMetric}>
                <Text variant='bodySmall' style={styles.patternMetricLabel}>
                  Strength
                </Text>
                <Text variant='bodyMedium' style={styles.patternMetricValue}>
                  {Math.round(pattern.strength * 100)}%
                </Text>
              </View>
            </View>

            {pattern.examples.length > 0 && (
              <View style={styles.patternExamples}>
                <Text variant='labelMedium' style={styles.examplesTitle}>
                  Examples:
                </Text>
                {pattern.examples.slice(0, 2).map((example, index) => (
                  <Text
                    key={index}
                    variant='bodySmall'
                    style={styles.exampleText}
                  >
                    • {example}
                  </Text>
                ))}
              </View>
            )}
          </View>
        </Card>
      ))}
    </ScrollView>
  );

  const renderAnomalies = () => (
    <ScrollView style={styles.anomaliesContainer}>
      {trendData.anomalies.map(anomaly => (
        <Card key={anomaly.id} style={styles.anomalyCard}>
          <TouchableOpacity
            onPress={() => onAnomalyInvestigate(anomaly)}
            style={styles.anomalyContent}
          >
            <View style={styles.anomalyHeader}>
              <Text variant='titleMedium' style={styles.anomalyType}>
                {anomaly.type.replace(/_/g, ' ').toUpperCase()}
              </Text>
              <Text variant='bodySmall' style={styles.anomalyDate}>
                {new Date(anomaly.timestamp).toLocaleDateString()}
              </Text>
            </View>

            <Text variant='bodyMedium' style={styles.anomalySkill}>
              Skill:{' '}
              {
                trendData.skillTrends.find(st => st.skillId === anomaly.skillId)
                  ?.skillName
              }
            </Text>

            <View style={styles.anomalyMetrics}>
              <Text variant='bodySmall' style={styles.anomalyMagnitude}>
                Magnitude: {Math.round(anomaly.magnitude * 100)}%
              </Text>
              <Text variant='bodySmall' style={styles.anomalyDuration}>
                Duration: {anomaly.duration} days
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.anomalyImpact,
                  {
                    color:
                      anomaly.impact === 'positive'
                        ? '#4CAF50'
                        : anomaly.impact === 'negative'
                          ? '#F44336'
                          : '#9E9E9E',
                  },
                ]}
              >
                {anomaly.impact.toUpperCase()} impact
              </Text>
            </View>

            {anomaly.possibleCauses.length > 0 && (
              <View style={styles.causesSection}>
                <Text variant='labelMedium' style={styles.causesTitle}>
                  Possible causes:
                </Text>
                <Text variant='bodySmall' style={styles.causeText}>
                  {anomaly.possibleCauses[0]}
                </Text>
              </View>
            )}

            <IconButton
              icon='chevron-right'
              size={16}
              style={styles.anomalyArrow}
            />
          </TouchableOpacity>
        </Card>
      ))}
    </ScrollView>
  );

  const renderContent = () => {
    switch (viewMode) {
      case 'overview':
        return renderOverview();
      case 'skills':
        return renderSkills();
      case 'predictions':
        return renderPredictions();
      case 'patterns':
        return renderPatterns();
      case 'anomalies':
        return renderAnomalies();
      default:
        return renderOverview();
    }
  };

  return (
    <Surface style={[styles.container, style]} elevation={1}>
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineSmall' style={styles.title}>
          Progress Trend Analysis
        </Text>

        {/* Tab Navigation */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.tabNavigation}
        >
          {[
            { key: 'overview', label: 'Overview', icon: 'chart-line' },
            { key: 'skills', label: 'Skills', icon: 'target' },
            { key: 'predictions', label: 'Predictions', icon: 'crystal-ball' },
            { key: 'patterns', label: 'Patterns', icon: 'sine-wave' },
            { key: 'anomalies', label: 'Anomalies', icon: 'alert-circle' },
          ].map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, viewMode === tab.key && styles.activeTab]}
              onPress={() => setViewMode(tab.key as ViewMode)}
            >
              <IconButton
                icon={tab.icon}
                size={16}
                iconColor={
                  viewMode === tab.key ? '#6750A4' : theme.colors.onSurface
                }
              />
              <Text
                variant='labelMedium'
                style={[
                  styles.tabLabel,
                  viewMode === tab.key && styles.activeTabLabel,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Content */}
      {renderContent()}
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: 16,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    marginBottom: 16,
    fontWeight: '600',
  },
  tabNavigation: {
    flexDirection: 'row',
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginRight: 8,
  },
  activeTab: {
    backgroundColor: 'rgba(103, 80, 164, 0.12)',
  },
  tabLabel: {
    marginLeft: 4,
  },
  activeTabLabel: {
    color: '#6750A4',
    fontWeight: '600',
  },

  // Overview Styles
  overviewContainer: {
    flex: 1,
    padding: 16,
  },
  overallTrendCard: {
    padding: 16,
    marginBottom: 16,
  },
  trendHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  trendInfo: {
    flex: 1,
  },
  trendTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  trendIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  trendDirection: {
    fontWeight: '600',
    marginLeft: 8,
  },
  trendMetrics: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
    paddingVertical: 12,
    backgroundColor: 'rgba(103, 80, 164, 0.05)',
    borderRadius: 8,
  },
  metricItem: {
    alignItems: 'center',
  },
  metricLabel: {
    opacity: 0.7,
    marginBottom: 4,
  },
  metricValue: {
    fontWeight: '600',
  },
  chart: {
    marginVertical: 8,
    borderRadius: 16,
  },
  categoryTrendsCard: {
    padding: 16,
    marginBottom: 16,
  },
  categoryTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  categoryItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  categoryInfo: {
    flex: 1,
  },
  categoryName: {
    fontWeight: '600',
  },
  categorySkillCount: {
    opacity: 0.7,
    marginTop: 2,
  },
  categoryTrend: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  categoryVelocity: {
    marginLeft: 4,
  },
  quickPredictionsCard: {
    padding: 16,
  },
  predictionsTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  predictionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  predictionContent: {
    flex: 1,
  },
  predictionDescription: {
    fontWeight: '600',
    marginBottom: 4,
  },
  predictionDate: {
    opacity: 0.7,
    marginBottom: 2,
  },
  predictionProbability: {
    fontWeight: '600',
  },

  // Skills Styles
  skillsContainer: {
    flex: 1,
    padding: 16,
  },
  skillSelector: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  skillChip: {
    marginRight: 8,
  },
  skillAnalysisCard: {
    padding: 16,
    marginBottom: 16,
  },
  skillAnalysisTitle: {
    fontWeight: '600',
    marginBottom: 16,
    textAlign: 'center',
  },
  correlationsSection: {
    marginTop: 16,
  },
  correlationsTitle: {
    fontWeight: '600',
    marginBottom: 8,
  },
  correlationItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 4,
  },
  correlationSkill: {
    flex: 1,
  },
  correlationValue: {
    fontWeight: '600',
  },
  skillsList: {
    gap: 8,
  },
  skillCard: {
    marginBottom: 8,
  },
  skillCardContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  skillInfo: {
    flex: 1,
  },
  skillName: {
    fontWeight: '600',
  },
  skillCategory: {
    opacity: 0.7,
    marginTop: 2,
  },
  skillTrendInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  skillVelocity: {
    marginLeft: 4,
  },

  // Predictions Styles
  predictionsContainer: {
    flex: 1,
    padding: 16,
  },
  predictionCard: {
    marginBottom: 12,
  },
  predictionCardContent: {
    padding: 16,
    position: 'relative',
  },
  predictionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  predictionTitle: {
    fontWeight: '600',
    flex: 1,
    marginRight: 8,
  },
  predictionDetails: {
    marginBottom: 12,
  },
  predictionDetailText: {
    marginBottom: 4,
  },
  predictionProbabilityText: {
    fontWeight: '600',
  },
  conditionsSection: {
    marginBottom: 8,
  },
  conditionsTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  conditionText: {
    opacity: 0.7,
    marginBottom: 2,
  },
  predictionArrow: {
    position: 'absolute',
    top: 16,
    right: 16,
  },

  // Patterns Styles
  patternsContainer: {
    flex: 1,
    padding: 16,
  },
  patternCard: {
    marginBottom: 12,
  },
  patternContent: {
    padding: 16,
  },
  patternHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  patternName: {
    fontWeight: '600',
    flex: 1,
  },
  patternType: {
    opacity: 0.7,
    textTransform: 'uppercase',
  },
  patternDescription: {
    marginBottom: 12,
    lineHeight: 20,
  },
  patternMetrics: {
    flexDirection: 'row',
    gap: 24,
    marginBottom: 12,
  },
  patternMetric: {
    alignItems: 'center',
  },
  patternMetricLabel: {
    opacity: 0.7,
    marginBottom: 4,
  },
  patternMetricValue: {
    fontWeight: '600',
  },
  patternExamples: {
    marginTop: 8,
  },
  examplesTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  exampleText: {
    opacity: 0.7,
    marginBottom: 2,
  },

  // Anomalies Styles
  anomaliesContainer: {
    flex: 1,
    padding: 16,
  },
  anomalyCard: {
    marginBottom: 12,
  },
  anomalyContent: {
    padding: 16,
    position: 'relative',
  },
  anomalyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  anomalyType: {
    fontWeight: '600',
    color: '#F57C00',
  },
  anomalyDate: {
    opacity: 0.7,
  },
  anomalySkill: {
    marginBottom: 12,
  },
  anomalyMetrics: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
  },
  anomalyMagnitude: {
    opacity: 0.7,
  },
  anomalyDuration: {
    opacity: 0.7,
  },
  anomalyImpact: {
    fontWeight: '600',
  },
  causesSection: {
    marginBottom: 8,
  },
  causesTitle: {
    fontWeight: '600',
    marginBottom: 4,
  },
  causeText: {
    opacity: 0.7,
  },
  anomalyArrow: {
    position: 'absolute',
    top: 16,
    right: 16,
  },
});
